// Authentication Module
import { state, updateState, clearState } from './state.js';
import { api, safeReadJson } from './api-client.js';
import { showNotification } from '../utils/notifications.js';

let pendingMfaToken = null;

function showError(elId, message) {
    const errorDiv = document.getElementById(elId);
    if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.classList.remove('hidden');
    } else {
        showNotification(message, 'error');
    }
}

function hideError(elId) {
    const errorDiv = document.getElementById(elId);
    if (errorDiv) {
        errorDiv.textContent = '';
        errorDiv.classList.add('hidden');
    }
}

function setFormVisible(step) {
    const loginForm = document.getElementById('login-form');
    const verifyForm = document.getElementById('mfa-verify-form');
    const setupForm = document.getElementById('mfa-setup-form');
    if (loginForm) loginForm.classList.toggle('hidden', step !== 'login');
    if (verifyForm) verifyForm.classList.toggle('hidden', step !== 'verify');
    if (setupForm) setupForm.classList.toggle('hidden', step !== 'setup');
}

function persistSession(data) {
    updateState({
        authToken: data.token,
        user: data.user,
        permissions: data.permissions || []
    });
    localStorage.setItem('auth_token', data.token);
    localStorage.setItem('user_data', JSON.stringify(data.user));
    localStorage.setItem('permissions', JSON.stringify(data.permissions || []));
    document.cookie = `auth_token=${data.token}; path=/; max-age=86400; SameSite=Strict`;
}

export function resetLoginMFA() {
    pendingMfaToken = null;
    hideError('login-error');
    hideError('mfa-error');
    hideError('mfa-setup-error');
    const code = document.getElementById('mfa-code');
    const setupCode = document.getElementById('mfa-setup-code');
    if (code) code.value = '';
    if (setupCode) setupCode.value = '';
    setFormVisible('login');
    const submitBtn = document.querySelector('#login-form button[type="submit"]');
    if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = 'LOGIN';
    }
}

export async function handleLogin(event) {
    event.preventDefault();
    hideError('login-error');

    try {
        const username = event.target.querySelector('input[type="text"]').value;
        const password = event.target.querySelector('input[type="password"]').value;

        const submitBtn = event.target.querySelector('button[type="submit"]');
        const originalText = submitBtn ? submitBtn.innerHTML : 'LOGIN';
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>AUTHENTICATING...';
        }

        const response = await fetch('/api/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });

        if (!response.ok) {
            const error = await safeReadJson(response, { error: 'Login failed' });
            throw new Error((error && error.error) || 'Login failed');
        }

        const data = await safeReadJson(response, null);
        if (!data || typeof data !== 'object') {
            throw new Error('Invalid login response');
        }

        if (data.mfa_required && data.mfa_token) {
            pendingMfaToken = data.mfa_token;
            setFormVisible('verify');
            const codeInput = document.getElementById('mfa-code');
            if (codeInput) codeInput.focus();
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = originalText;
            }
            return;
        }

        if (data.mfa_setup_required && data.mfa_token) {
            pendingMfaToken = data.mfa_token;
            const qr = document.getElementById('mfa-qr');
            const secretEl = document.getElementById('mfa-secret');
            if (qr && data.qr_code) qr.src = data.qr_code;
            if (secretEl) secretEl.textContent = data.secret || '';
            setFormVisible('setup');
            const setupCode = document.getElementById('mfa-setup-code');
            if (setupCode) setupCode.focus();
            if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = originalText;
            }
            return;
        }

        if (!data.token) {
            throw new Error('Invalid login response');
        }

        persistSession(data);
        console.log('[Auth] Login successful, redirecting...');
        window.location.href = '/';

    } catch (error) {
        console.error('[Auth] Login failed:', error);

        const submitBtn = event.target.querySelector('button[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = 'LOGIN';
        }

        showError('login-error', error.message);
    }
}

export async function handleMFAVerify(event) {
    event.preventDefault();
    hideError('mfa-error');

    const submitBtn = event.target.querySelector('button[type="submit"]');
    try {
        const code = (document.getElementById('mfa-code') || {}).value || '';
        if (!pendingMfaToken) {
            throw new Error('MFA session expired — please log in again');
        }
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>VERIFYING...';
        }

        const response = await fetch('/api/login/mfa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mfa_token: pendingMfaToken, code: code.trim() })
        });

        if (!response.ok) {
            const error = await safeReadJson(response, { error: 'Invalid MFA code' });
            throw new Error((error && error.error) || 'Invalid MFA code');
        }

        const data = await safeReadJson(response, null);
        if (!data || !data.token) {
            throw new Error('Invalid MFA response');
        }

        persistSession(data);
        window.location.href = '/';
    } catch (error) {
        console.error('[Auth] MFA verify failed:', error);
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = 'VERIFY';
        }
        showError('mfa-error', error.message);
    }
}

export async function handleMFASetup(event) {
    event.preventDefault();
    hideError('mfa-setup-error');

    const submitBtn = event.target.querySelector('button[type="submit"]');
    try {
        const code = (document.getElementById('mfa-setup-code') || {}).value || '';
        if (!pendingMfaToken) {
            throw new Error('MFA session expired — please log in again');
        }
        if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin mr-2"></i>ENABLING...';
        }

        const response = await fetch('/api/mfa/enable', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mfa_token: pendingMfaToken, code: code.trim() })
        });

        if (!response.ok) {
            const error = await safeReadJson(response, { error: 'Failed to enable MFA' });
            throw new Error((error && error.error) || 'Failed to enable MFA');
        }

        const data = await safeReadJson(response, null);
        if (!data || !data.token) {
            throw new Error('Invalid MFA setup response');
        }

        persistSession(data);
        window.location.href = '/';
    } catch (error) {
        console.error('[Auth] MFA setup failed:', error);
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.innerHTML = 'Enable MFA & Continue';
        }
        showError('mfa-setup-error', error.message);
    }
}

export function handleLogout() {
    try {
        clearState();
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('permissions');
        localStorage.removeItem('current_view');

        document.cookie = "auth_token=; path=/; max-age=0; SameSite=Strict";

        console.log('[Auth] Logout successful, redirecting...');
        window.location.href = '/login';
    } catch (error) {
        console.error('[Auth] Logout error:', error);
    }
}

/** Refreshes permissions from GET /api/me (fixes stale localStorage and old login responses). */
export async function refreshSession() {
    const token = localStorage.getItem('auth_token');
    if (!token) {
        return;
    }
    try {
        const response = await fetch('/api/me', {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
            return;
        }
        const data = await safeReadJson(response, {});
        let perms = data && data.permissions;
        if (!Array.isArray(perms)) {
            perms = [];
        }
        const permStrings = perms.map(p => (typeof p === 'string' ? p : String(p)));
        updateState({ permissions: permStrings });
        localStorage.setItem('permissions', JSON.stringify(permStrings));
        console.log('[Auth] Session permissions refreshed:', permStrings.length);
    } catch (e) {
        console.warn('[Auth] refreshSession failed:', e);
    }
}

export function initAutoLogin() {
    try {
        const token = localStorage.getItem('auth_token');

        if (token) {
            if (window.location.pathname === '/login') {
                console.log('[Auth] Already authenticated, redirecting to dashboard...');
                window.location.href = '/';
                return;
            }
            console.log('[Auth] Session active');
        } else {
            if (window.location.pathname !== '/login') {
                console.log('[Auth] No session, redirecting to login...');
                window.location.href = '/login';
            }
        }
    } catch (error) {
        console.error('[Auth] Auto-login error:', error);
    }
}

window.handleLogin = handleLogin;
window.handleLogout = handleLogout;
window.handleMFAVerify = handleMFAVerify;
window.handleMFASetup = handleMFASetup;
window.resetLoginMFA = resetLoginMFA;
